import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FiSearch, FiX, FiZap, FiPackage, FiUser, FiClock, FiCommand } from 'react-icons/fi';

const COMMAND_TYPES = {
  ticket: { icon: FiZap, color: 'text-amber-500 bg-amber-50' },
  order: { icon: FiPackage, color: 'text-indigo-500 bg-indigo-50' },
  action: { icon: FiCommand, color: 'text-emerald-500 bg-emerald-50' },
  recent: { icon: FiClock, color: 'text-zinc-500 bg-zinc-50' },
};

export default function CommandPalette({
  isOpen,
  onClose,
  tickets = [],
  orders = [],
  onSelectTicket,
  onSelectOrder,
  onAction,
}) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Build search results
  const results = useMemo(() => {
    const q = query.toLowerCase().trim();
    const items = [];

    // Static actions
    const actions = [
      { id: 'action-claim', type: 'action', label: 'Claim top ticket', action: 'claim' },
      { id: 'action-refresh', type: 'action', label: 'Refresh queue', action: 'refresh' },
      { id: 'action-focus', type: 'action', label: 'Toggle focus mode', action: 'focus' },
      { id: 'action-mute', type: 'action', label: 'Toggle sounds', action: 'mute' },
    ];

    if (!q) {
      // Show actions and recent items when no query
      items.push(...actions.slice(0, 3));
      items.push(...tickets.slice(0, 3).map(t => ({
        id: `ticket-${t.id}`,
        type: 'ticket',
        label: t.name || t.type,
        sublabel: `Order #${t.orderUniqueId}`,
        data: t,
      })));
    } else {
      // Filter actions
      actions.forEach(a => {
        if (a.label.toLowerCase().includes(q)) {
          items.push(a);
        }
      });

      // Filter tickets
      tickets.forEach(t => {
        const matchName = t.name?.toLowerCase().includes(q);
        const matchOrder = t.orderUniqueId?.toLowerCase().includes(q);
        const matchType = t.type?.toLowerCase().includes(q);
        if (matchName || matchOrder || matchType) {
          items.push({
            id: `ticket-${t.id}`,
            type: 'ticket',
            label: t.name || t.type,
            sublabel: `Order #${t.orderUniqueId}`,
            data: t,
          });
        }
      });

      // Filter orders
      orders.forEach(o => {
        const matchId = o.uniqueOrderId?.toLowerCase().includes(q);
        const matchGuest = o.guest?.name?.toLowerCase().includes(q);
        const matchProperty = o.property?.name?.toLowerCase().includes(q);
        if (matchId || matchGuest || matchProperty) {
          items.push({
            id: `order-${o._id}`,
            type: 'order',
            label: `Order #${o.uniqueOrderId}`,
            sublabel: o.guest?.name || o.property?.name,
            data: o,
          });
        }
      });
    }

    return items.slice(0, 10);
  }, [query, tickets, orders]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        handleSelect(results[selectedIndex]);
        break;
      case 'Escape':
        onClose();
        break;
      default:
        break;
    }
  };

  const handleSelect = (item) => {
    if (!item) return;
    
    switch (item.type) {
      case 'ticket':
        onSelectTicket?.(item.data);
        break;
      case 'order':
        onSelectOrder?.(item.data);
        break;
      case 'action':
        onAction?.(item.action);
        break;
      default:
        break;
    }
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fade-in"
        onClick={onClose}
      />
      
      {/* Palette */}
      <div className="fixed inset-x-0 top-[20%] flex justify-center z-50 p-4 pointer-events-none">
        <div 
          className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-scale-in pointer-events-auto"
          onClick={e => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-4 border-b border-zinc-100">
            <FiSearch className="w-5 h-5 text-zinc-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search tickets, orders, or actions..."
              className="flex-1 bg-transparent border-0 outline-none text-zinc-900 placeholder:text-zinc-400"
            />
            <div className="flex items-center gap-1 text-xs text-zinc-400">
              <kbd className="px-1.5 py-0.5 rounded bg-zinc-100 font-mono">esc</kbd>
              <span>to close</span>
            </div>
          </div>

          {/* Results */}
          <div className="max-h-80 overflow-y-auto">
            {results.length === 0 ? (
              <div className="p-8 text-center">
                <FiSearch className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">No results found</p>
              </div>
            ) : (
              <div className="p-2">
                {results.map((item, idx) => {
                  const config = COMMAND_TYPES[item.type] || COMMAND_TYPES.action;
                  const Icon = config.icon;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleSelect(item)}
                      className={`
                        w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors
                        ${idx === selectedIndex ? 'bg-indigo-50' : 'hover:bg-zinc-50'}
                      `}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-900 truncate">{item.label}</p>
                        {item.sublabel && (
                          <p className="text-xs text-zinc-500 truncate">{item.sublabel}</p>
                        )}
                      </div>
                      <span className="text-xs text-zinc-400 capitalize">{item.type}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-zinc-50 border-t border-zinc-100 flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs text-zinc-500">
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 rounded bg-zinc-200 font-mono">↑↓</kbd> navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 rounded bg-zinc-200 font-mono">↵</kbd> select
              </span>
            </div>
            <span className="text-xs text-zinc-400">
              {results.length} result{results.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

